import { CheckCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { Table as AntTable, Button, Card, Flex, Tag, Typography } from 'antd';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';

import {
    NurseProfileDto,
    ShiftListItemDto,
    ShiftStatus,
} from '@clh/api-client';
import { HumlaDateTime, roundToAtMostTwoDecimals } from '@clh/util';

import { useApiClient } from '../hooks/use-api-client';
import Spinner from '../spinner';

import usePayPeriodContext from './pay-period-context';

const { Text } = Typography;

function ShiftSummary({ shifts }: { shifts: ShiftListItemDto[] }) {
    const totalShifts = shifts.length;
    const shiftsAwaitingTimeSubmission = shifts.filter(
        (shift) => !shift.minutesWorked && !shift.didNotWork
    ).length;

    return (
        <Card size="small" style={{ marginBottom: 16 }}>
            <Flex justify="space-between" align="center">
                <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
                    Shift Summary
                </Typography.Title>
                <Flex gap={24}>
                    {shiftsAwaitingTimeSubmission > 0 && (
                        <div style={{ textAlign: 'center' }}>
                            <div
                                style={{
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: 'red',
                                }}
                            >
                                {shiftsAwaitingTimeSubmission}
                            </div>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Awaiting Time Submission
                            </Text>
                        </div>
                    )}
                    <div style={{ textAlign: 'center' }}>
                        <div
                            style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: '#52c41a',
                            }}
                        >
                            {totalShifts}
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            Assigned & Active Shifts
                        </Text>
                    </div>
                </Flex>
            </Flex>
        </Card>
    );
}

export default function PayrollShifts() {
    const api = useApiClient();
    const [shifts, setShifts] = useState<ShiftListItemDto[]>([]);
    const [loading, setLoading] = useState(false);

    const { startDate: payPeriodStart, endDate: payPeriodEnd } =
        usePayPeriodContext();

    const fetchShifts = async () => {
        if (!api) return;

        setLoading(true);
        try {
            const response = await api.shiftControllerListShifts({
                after: payPeriodStart,
                before: payPeriodEnd,
                pageSize: 1000,
                assigned: true,
                status: ShiftStatus.Active,
            });

            const assignedOnly = response.result.filter(
                (s) => s.nurseProfile?.id
            );

            setShifts(assignedOnly);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchShifts();
    }, [payPeriodStart.toISOString(), payPeriodEnd.toISOString()]);

    if (!api) {
        return <Spinner />;
    }

    if (loading) {
        return <Spinner />;
    }

    const payPeriodForShift = (shift: ShiftListItemDto) =>
        shift.payPeriods?.find(
            (pp) => pp.startDate >= payPeriodStart && pp.endDate <= payPeriodEnd
        );

    const columns = [
        {
            title: 'Facility',
            dataIndex: 'facility',
            render: (facility: any, shift: ShiftListItemDto) => (
                <Button
                    type="link"
                    target="_blank"
                    href={`/shifts/${shift.id}`}
                    icon={<LinkOutlined />}
                    style={{ padding: 0, height: 'auto' }}
                >
                    {facility.name}
                </Button>
            ),
            ellipsis: true,
        },
        {
            dataIndex: 'shiftType',
            render: (shiftType: string, shift: ShiftListItemDto) => (
                <>
                    <Tag>{shiftType.toLowerCase()}</Tag>
                    {shift.specialtyCode && <Tag>{shift.specialtyCode}</Tag>}
                </>
            ),
        },
        {
            title: 'Scheduled Time',
            dataIndex: 'startTime',
            width: 280,
            render: (_: any, shift: ShiftListItemDto) => (
                <Typography.Text type="secondary">
                    {DateTime.fromJSDate(shift.startTime).toLocaleString(
                        HumlaDateTime.DATETIME_SHORT
                    )}{' '}
                    -{' '}
                    {DateTime.fromJSDate(shift.endTime).toLocaleString(
                        HumlaDateTime.DATETIME_SHORT
                    )}
                </Typography.Text>
            ),
        },
        {
            title: 'Assigned Nurse',
            dataIndex: 'nurseProfile',
            render: (nurseProfile?: NurseProfileDto | null) =>
                nurseProfile && (
                    <Button
                        type="link"
                        target="_blank"
                        href={`/profiles/${nurseProfile?.id}`}
                        style={{ padding: 0, height: 'auto' }}
                    >
                        {nurseProfile.firstName} {nurseProfile.lastName}
                    </Button>
                ),
        },
        {
            title: 'Hours Submitted',
            dataIndex: 'minutesWorked',
            render: (minutesWorked: number | null, shift: ShiftListItemDto) => {
                const payPeriod = payPeriodForShift(shift);

                if (shift.didNotWork) {
                    return <b>Did not work</b>;
                }
                if (!minutesWorked && !shift.onCallShift?.minutesWorked) {
                    return (
                        <Typography.Text type="secondary">
                            Not submitted
                        </Typography.Text>
                    );
                }

                const totalTimeSubmitted = roundToAtMostTwoDecimals(
                    ((minutesWorked || 0) +
                        (shift.onCallShift?.minutesWorked || 0)) /
                        60
                );
                const payPeriodHours =
                    payPeriod &&
                    (payPeriod.minutesWorked || payPeriod.onCallMinutesWorked)
                        ? roundToAtMostTwoDecimals(
                              ((payPeriod.minutesWorked || 0) +
                                  (payPeriod.onCallMinutesWorked || 0)) /
                                  60
                          )
                        : null;

                if (payPeriodHours && payPeriodHours < totalTimeSubmitted) {
                    return `${payPeriodHours} / ${totalTimeSubmitted}`;
                }

                if (payPeriodHours) {
                    return payPeriodHours;
                }

                return totalTimeSubmitted;
            },
        },

        {
            title: 'Hours Scheduled',
            render: (_: any, shift: ShiftListItemDto) => {
                const startTime = DateTime.fromJSDate(shift.startTime);
                const endTime = DateTime.fromJSDate(shift.endTime);
                const scheduledMinutes = endTime.diff(
                    startTime,
                    'minutes'
                ).minutes;
                const scheduledHours = roundToAtMostTwoDecimals(
                    scheduledMinutes / 60
                );
                return scheduledHours;
            },
        },
        {
            title: 'Invoiced',
            render: (_: any, shift: ShiftListItemDto) =>
                payPeriodForShift(shift)?.stripeInvoiceId && (
                    <CheckCircleOutlined />
                ),
        },
        {
            title: 'Paid',
            render: (_: any, shift: ShiftListItemDto) =>
                payPeriodForShift(shift)?.payrollEmployeeCheckId && (
                    <CheckCircleOutlined />
                ),
        },
    ];

    return (
        <>
            <ShiftSummary shifts={shifts} />
            <Card size="small">
                <AntTable<ShiftListItemDto>
                    size="small"
                    columns={columns}
                    dataSource={shifts}
                    rowKey="id"
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} shifts`,
                    }}
                />
            </Card>
        </>
    );
}
